/* eslint-disable react-hooks/exhaustive-deps */
import { Tabs } from "antd";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import Analytics from "../../components/ExternalProblemsManagement/Analytics";
import Catalogue from "../../components/ExternalProblemsManagement/Catalogue";
import { LabelWithHint } from "../../components/ExternalProblemsManagement/InfoHint";
import ReconciliationQueue from "../../components/ExternalProblemsManagement/ReconciliationQueue";
import TagMappings from "../../components/ExternalProblemsManagement/TagMappings";
import Navbar from "../../components/Navbar";
import { backendUrlByEnv, LOGIN } from "../../constants/routes";
import { clamp } from "../../utils/responsiveHelpers";

const EXTERNAL_PROBLEMS_TAB_KEYS = Object.freeze({
  RECONCILIATION_QUEUE: "reconciliationQueue",
  CATALOGUE: "catalogue",
  TAG_MAPPINGS: "tagMappings",
  ANALYTICS: "analytics"
});

/** One plain sentence per tab, for an admin opening this page for the first time. */
const EXTERNAL_PROBLEMS_TAB_INFO_TEXT = Object.freeze({
  [EXTERNAL_PROBLEMS_TAB_KEYS.RECONCILIATION_QUEUE]:
    "Problems students asked about that we could not answer from stored data. Each row waits here until it is filled in, or ruled out.",
  [EXTERNAL_PROBLEMS_TAB_KEYS.CATALOGUE]:
    "Every problem from another site whose statement we store, so a student pasting its link gets an answer grounded in the real problem.",
  [EXTERNAL_PROBLEMS_TAB_KEYS.TAG_MAPPINGS]:
    "How a topic name used by another site is translated into the topic name we use, so the same topic reads the same everywhere.",
  [EXTERNAL_PROBLEMS_TAB_KEYS.ANALYTICS]:
    "How often a pasted problem link gets answered, which sites it fails on, and how the queue behind it is moving."
});

/**
 * The page's tabs, in display order. Each tab names the component it shows;
 * every tab receives `ROOT_URL` and `hasManagementAccess`, and ignores the
 * ones it has no use for.
 */
const EXTERNAL_PROBLEMS_TABS = Object.freeze([
  {
    key: EXTERNAL_PROBLEMS_TAB_KEYS.RECONCILIATION_QUEUE,
    label: "Reconciliation Queue",
    TabContent: ReconciliationQueue
  },
  {
    key: EXTERNAL_PROBLEMS_TAB_KEYS.CATALOGUE,
    label: "Catalogue",
    TabContent: Catalogue
  },
  {
    key: EXTERNAL_PROBLEMS_TAB_KEYS.TAG_MAPPINGS,
    label: "Tag Mappings",
    TabContent: TagMappings
  },
  {
    key: EXTERNAL_PROBLEMS_TAB_KEYS.ANALYTICS,
    label: "Analytics",
    TabContent: Analytics
  }
]);

/**
 * External problems admin page: the reconciliation queue, the catalogue, the
 * tag mappings and the analytics, as tabs. Redirects to login for an admin
 * without `externalProblems.hasAccess`; write controls inside the tabs need
 * `externalProblems.hasManagementAccess`.
 */
const ExternalProblemsManagement = () => {
  const [activeTabKey, setActiveTabKey] = useState(EXTERNAL_PROBLEMS_TAB_KEYS.RECONCILIATION_QUEUE);
  const navigate = useNavigate();
  const { config: userConfig, env } = useSelector((state) => state?.reducer?.authInfo) || {};

  const ROOT_URL = backendUrlByEnv(env);
  const { externalProblems: { hasAccess, hasManagementAccess = false } = {} } = userConfig ?? {};

  const tabItems = EXTERNAL_PROBLEMS_TABS.map(({ key, label, TabContent }) => ({
    key,
    label: <LabelWithHint label={label} infoText={EXTERNAL_PROBLEMS_TAB_INFO_TEXT[key]} />,
    children: <TabContent ROOT_URL={ROOT_URL} hasManagementAccess={hasManagementAccess} />
  }));

  useEffect(() => {
    if (!hasAccess) {
      navigate(LOGIN);
    }
  }, []);

  return (
    <>
      <Navbar />
      <Tabs
        activeKey={activeTabKey}
        onChange={setActiveTabKey}
        items={tabItems}
        tabBarStyle={{
          paddingLeft: clamp(12, 24),
          paddingRight: clamp(12, 24),
          marginBottom: 0
        }}
      />
    </>
  );
};

export default ExternalProblemsManagement;
